import { useState } from "react";
import {
  AlertTriangle,
  DownloadIcon,
  RefreshCw,
  ShieldAlert,
  ShieldCheck,
  ShieldQuestion,
} from "lucide-react";
import { useParams } from "react-router";
import { toast } from "sonner";

import { AuthLayout } from "@/components/auth/AuthLayout";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Spinner } from "@/components/ui/spinner";
import { useDocumentoVerificado } from "@/hooks/useVerificacionPublica";
import type { DocumentoVerificado, EstadoDeVerificacion } from "@/lib/verificacionPublica";
import { descargarDocumentoVerificado } from "@/lib/verificacionPublica";

/**
 * Pantalla a la que lleva el QR del sello impreso en el documento de
 * cotización.
 *
 * SIN LOGIN, y es la segunda pantalla del sistema en esa condición después de
 * `AceptarCotizacion`. Quien llega acá puede no ser siquiera el cliente: un
 * banco que recibió el presupuesto, un perito de la contraparte, un tribunal.
 * Ninguno va a tener cuenta en Consultek.
 *
 * NO ES UNA PANTALLA DE FIRMA Y NO USA ESA PALABRA. Lo que reemplazó en el
 * documento fueron dos firmas impresas, pero esto no es una firma electrónica
 * —ni simple ni avanzada, en los términos de la ley 19.799— y decirlo
 * afirmaría algo que el sistema no hace. Lo que sí hace es dejar contrastar
 * contra la base lo que el papel imprime.
 *
 * EXISTE PARA COMPARAR, NO PARA INFORMAR, y de ahí sale toda la disposición:
 * los campos están en el mismo orden en que aparecen en el sello y con las
 * mismas palabras, para que el cotejo sea recorrer dos columnas. Agregar acá
 * un dato que el documento no imprime no ayudaría a verificar nada — solo lo
 * publicaría.
 *
 * EL ESTADO VA PRIMERO Y EN GRANDE porque es lo único que el papel no puede
 * saber de sí mismo. Un presupuesto impreso no sabe que lo reemplazaron: quien
 * lo tiene lee un precio que Consultek ya no ofrece y nada en la hoja lo
 * delata. Eso es lo que esta pantalla aporta y que ninguna firma aportaría.
 *
 * UN SOLO RECHAZO, el 404, al revés que su vecina de aceptación, que tiene
 * tres. Que la cotización esté anulada, vencida o ya aceptada NO es un error:
 * llega con 200 y su estado en el cuerpo. Tratarlas como fallas dejaría a
 * quien tiene el papel sin saber si el documento es falso o si simplemente hay
 * una versión más nueva, que son cosas opuestas.
 */
export function VerificarCotizacion() {
  const { token } = useParams<{ token: string }>();
  const consulta = useDocumentoVerificado(token);

  // Enlace sin código: ni vale la pena pegarle a la API.
  if (!token) {
    return (
      <Aviso
        icon={<AlertTriangle className="h-6 w-6" />}
        tono="error"
        titulo="Código incompleto"
        descripcion="Este enlace no trae el código de verificación. Vuelve a escanear el código del documento, o escribe la dirección completa que aparece impresa debajo."
      />
    );
  }

  if (consulta.isLoading) {
    return (
      <AuthLayout>
        <div className="flex flex-col items-center gap-3 py-12 text-center">
          <Spinner className="text-primary size-6" />
          <p className="text-ink-muted text-caption">Verificando el documento…</p>
        </div>
      </AuthLayout>
    );
  }

  if (consulta.isError || !consulta.data) {
    return (
      <Aviso
        icon={<ShieldAlert className="h-6 w-6" />}
        tono="error"
        titulo="No encontramos este documento"
        descripcion="Ningún documento emitido por Consultek corresponde a este código. Revisa que lo hayas copiado entero, y si el código está impreso en un papel que recibiste, escríbenos antes de darlo por válido."
      />
    );
  }

  const documento = consulta.data;
  const lectura = LECTURAS[documento.estado];

  return (
    <AuthLayout>
      <div className="mb-8">
        <div className={`mb-5 flex h-12 w-12 items-center justify-center rounded-2xl ${lectura.fondo}`}>
          {lectura.icono}
        </div>

        <h1 className="text-h1 text-ink">{lectura.titulo}</h1>

        <div className="mt-3">
          <Badge variant={lectura.badge}>{lectura.rotulo}</Badge>
        </div>

        <p className="text-ink-muted text-caption mt-3">{lectura.descripcion(documento)}</p>

        {/* LOS DATOS A CONTRASTAR, en el mismo orden que el sello impreso.
            Quien verifica está mirando dos cosas a la vez —el papel y la
            pantalla—, así que lo que se le puede ahorrar es tener que buscar
            dónde quedó cada campo. */}
        <dl className="bg-muted/50 text-body mt-6 space-y-3 rounded-xl border p-5">
          <Dato rotulo="Propuesta N°" valor={documento.folio} />
          <Dato rotulo="Cliente" valor={documento.cliente} />
          <Dato rotulo="Propiedad" valor={documento.propiedad} />
          <Dato rotulo="Emitido por" valor={documento.emitida_por} />
          <Dato
            rotulo="Fecha de emisión"
            valor={documento.emitida_en && `${documento.emitida_en} h`}
          />
          <Dato rotulo="Vigencia de la oferta" valor={documento.vigencia_hasta} />

          <div className="flex items-baseline justify-between gap-4 border-t pt-3">
            <dt className="text-ink-muted">Total</dt>
            {/* Ya viene con su unidad desde el API: la pantalla no asume la
                moneda ni la recalcula. */}
            <dd className="text-primary text-lg font-extrabold">{documento.total_uf}</dd>
          </div>
        </dl>

        {/* EL DOCUMENTO, PARA BAJARLO. Va DESPUÉS del cuadro de datos y antes
            de la huella a propósito: quien llega acá casi siempre tiene el
            papel y no el archivo —lo recibió impreso, reenviado o dentro de un
            expediente—, así que el cotejo de arriba es lo primero que necesita
            y el archivo es lo que se lleva. Puesto antes de los datos
            competiría con el estado, que es lo que casi todos vienen a mirar.

            SE OMITE cuando no hay documento que entregar, y no se muestra
            deshabilitado: un botón apagado en una pantalla sin login no tiene
            cómo explicar por qué, y quien tiene el papel leería una falla del
            sistema donde lo que hay es un documento que nunca se emitió — algo
            que el bloque de arriba ya le contó con todas las letras. */}
        {documento.documento_descargable && <BotonDescargarDocumento token={token} />}

        {/* LA HUELLA VA PLEGADA. No le sirve a la mayoría —nadie calcula un
            sha256 a ojo— pero sí al perito, al abogado y al que discute un
            documento en un arbitraje, que es el escenario para el que este
            sello existe. Desplegada le robaría la atención al estado, que es
            lo que casi todos vienen a mirar. */}
        {documento.documento_hash && (
          <details className="mt-4">
            <summary className="text-ink-muted text-caption cursor-pointer select-none">
              Huella digital del archivo (SHA-256)
            </summary>
            <p className="text-ink-hint mt-2 text-caption">
              Corresponde a los bytes exactos del PDF que Consultek envió. Si tienes el archivo,
              su huella debe coincidir carácter por carácter con esta.
            </p>
            <code className="text-ink-body mt-2 block break-all rounded-md border bg-muted/50 p-3 font-mono text-caption">
              {documento.documento_hash}
            </code>
          </details>
        )}

        {/* LO QUE ESTA PÁGINA NO ES. Va explícito y no en letra chica de
            descargo: alguien que llega acá desde un papel puede razonablemente
            creer que está leyendo una firma electrónica, y dejar esa
            interpretación en pie sería dejar que el sistema afirme algo que no
            hace. */}
        <p className="text-ink-hint text-caption mt-6">
          Esta verificación no es una firma electrónica: confirma que el documento fue emitido
          por Consultek y muestra en qué situación está hoy. Si algo no coincide con el papel que
          tienes, escríbenos antes de usarlo.
        </p>
      </div>
    </AuthLayout>
  );
}

/**
 * Baja el PDF que corresponde a este sello.
 *
 * ES EL ORIGINAL ARCHIVADO, no un documento que se vuelva a armar: el servidor
 * entrega los mismos bytes cuyo sha256 esta pantalla publica dos bloques más
 * abajo. Si devolviera un render nuevo, la huella impresa dejaría de coincidir
 * con el archivo que la propia página acaba de entregar.
 *
 * MUESTRA QUE ESTÁ TRABAJANDO por lo mismo que su gemelo interno: el documento
 * pesa cerca de un mega, y sin señal la reacción natural es volver a apretar.
 *
 * UN SOLO MENSAJE DE ERROR, y no por pereza: acá no hay sesión que pueda haber
 * vencido ni permisos que revisar, así que lo que falló es la red o el
 * servidor, y las dos se resuelven igual. Lo que el mensaje no hace es poner
 * en duda el documento — quien lee esta pantalla está decidiendo si un papel
 * es auténtico, y una descarga que no salió no dice nada sobre eso.
 */
function BotonDescargarDocumento({ token }: { token: string }) {
  const [descargando, setDescargando] = useState(false);

  async function descargar() {
    setDescargando(true);

    try {
      await descargarDocumentoVerificado(token);
    } catch {
      toast.error("No pudimos descargar el documento. Vuelve a intentarlo en un momento.");
    } finally {
      setDescargando(false);
    }
  }

  return (
    <Button
      type="button"
      variant="outline"
      className="mt-4 w-full"
      onClick={descargar}
      disabled={descargando}
    >
      {descargando ? <Spinner /> : <DownloadIcon className="size-4" />}
      Descargar el documento (PDF)
    </Button>
  );
}

/**
 * Un renglón del cotejo. Se OMITE cuando no hay valor en vez de imprimir un
 * guion: la pantalla se lee contra un papel, y un renglón vacío haría dudar de
 * si el dato falta en el sistema o si se leyó mal el documento.
 */
function Dato({ rotulo, valor }: { rotulo: string; valor: string | null }) {
  if (!valor) {
    return null;
  }

  return (
    <div className="flex items-baseline justify-between gap-4">
      <dt className="text-ink-muted">{rotulo}</dt>
      <dd className="text-right font-medium">{valor}</dd>
    </div>
  );
}

/**
 * Cómo se le cuenta cada estado a quien tiene el papel.
 *
 * Es un mapa y no una cadena de `if`: el tipo obliga a cubrir los cinco casos,
 * así que un estado nuevo en el API rompe la compilación en vez de caer en un
 * texto por defecto que le diría algo inventado sobre un documento real.
 *
 * LOS CUATRO PRIMEROS DICEN QUE EL DOCUMENTO ES AUTÉNTICO, y solo cambian
 * sobre su vigencia. Es la distinción que ordena toda la pantalla: "no es
 * válido" y "ya no está vigente" son cosas opuestas, y fundirlas haría que un
 * cliente descartara como falso un presupuesto que Consultek sí emitió.
 */
const LECTURAS: Record<
  EstadoDeVerificacion,
  {
    icono: React.ReactNode;
    fondo: string;
    badge: "success" | "warning" | "info";
    rotulo: string;
    titulo: string;
    descripcion: (documento: DocumentoVerificado) => string;
  }
> = {
  vigente: {
    icono: <ShieldCheck className="h-6 w-6" />,
    fondo: "bg-success-soft text-success-soft-foreground",
    badge: "success",
    rotulo: "Vigente",
    titulo: "Documento verificado",
    descripcion: (documento) =>
      documento.vigencia_hasta
        ? `Este presupuesto fue emitido por Consultek y su oferta está vigente hasta el ${documento.vigencia_hasta}.`
        : "Este presupuesto fue emitido por Consultek y su oferta está vigente.",
  },

  aceptada: {
    icono: <ShieldCheck className="h-6 w-6" />,
    fondo: "bg-success-soft text-success-soft-foreground",
    badge: "success",
    rotulo: "Aceptada",
    titulo: "Documento verificado",
    descripcion: () =>
      "Este presupuesto fue emitido por Consultek y ya fue aceptado. El trabajo está en curso.",
  },

  vencida: {
    icono: <ShieldCheck className="h-6 w-6" />,
    fondo: "bg-warning-soft text-warning-soft-foreground",
    badge: "warning",
    rotulo: "Oferta vencida",
    titulo: "Documento verificado",
    descripcion: (documento) =>
      documento.vigencia_hasta
        ? `Los datos coinciden con lo que emitió Consultek, pero la oferta venció el ${documento.vigencia_hasta}. El precio puede haber cambiado: escríbenos y te enviamos un presupuesto actualizado.`
        : "Los datos coinciden con lo que emitió Consultek, pero la oferta ya venció. Escríbenos y te enviamos un presupuesto actualizado.",
  },

  // El caso que justifica la página: el papel es auténtico Y está reemplazado,
  // y no hay forma de que la hoja impresa lo diga por sí sola.
  anulada: {
    icono: <RefreshCw className="h-6 w-6" />,
    fondo: "bg-warning-soft text-warning-soft-foreground",
    badge: "warning",
    rotulo: "Reemplazado",
    titulo: "Hay un presupuesto más nuevo",
    // SIN NUMERAR LA VERSIÓN. Decía "corresponde a la versión 2", que es el
    // recuento de recotizaciones de Consultek: quien lee esto recibió UN
    // presupuesto y ese número le hace suponer otros que nunca le llegaron.
    // Lo que necesita saber es que el que tiene en la mano ya no vale.
    descripcion: () =>
      "Este documento es auténtico, pero Consultek lo reemplazó por un presupuesto más nuevo. No uses estas cifras: pídenos el vigente.",
  },

  no_emitida: {
    icono: <ShieldQuestion className="h-6 w-6" />,
    fondo: "bg-info-soft text-info-soft-foreground",
    badge: "info",
    rotulo: "No emitido",
    titulo: "Este documento no fue enviado",
    descripcion: () =>
      "El documento existe en el sistema de Consultek, pero todavía no se emitió a ningún cliente. Si lo recibiste, escríbenos antes de usarlo.",
  },
};

/**
 * Pantalla de una sola pieza para lo que no llegó a verificarse.
 *
 * SIN BOTÓN, igual que su gemela de `AceptarCotizacion` y por el mismo motivo:
 * quien lee esto no tiene cuenta, así que un enlace al login sería mandarlo a
 * una puerta que no puede abrir. La salida es escribirle a Consultek, y eso lo
 * dice la descripción.
 */
function Aviso({
  icon,
  tono,
  titulo,
  descripcion,
}: {
  icon: React.ReactNode;
  tono: "error" | "neutro";
  titulo: string;
  descripcion: string;
}) {
  const fondo = {
    error: "bg-destructive/10 text-destructive",
    neutro: "bg-muted text-ink-muted",
  }[tono];

  return (
    <AuthLayout>
      <div className="mb-8">
        <div className={`mb-5 flex h-12 w-12 items-center justify-center rounded-2xl ${fondo}`}>
          {icon}
        </div>
        <h1 className="text-h1 text-ink">{titulo}</h1>
        <p className="text-ink-muted text-caption mt-2">{descripcion}</p>
      </div>
    </AuthLayout>
  );
}
